/**
 * บัญชีล็อกอินของพนักงานหนึ่งคน — ออกบัญชีครั้งแรก หรือรีเซ็ตรหัส
 *
 * รหัสชั่วคราวมาจากเซิร์ฟเวอร์ครั้งเดียว: อยู่ใน state ของแผงนี้เท่านั้น ไม่ลง storage, URL หรือ log
 * หายเมื่อกด "ปิด" หรือเลือกพนักงานคนอื่น (ผู้เรียก mount ใหม่ด้วย `key` ต่อพนักงาน)
 * เป็น UX layer เท่านั้น เซิร์ฟเวอร์ตัดสินทุกคำขอ (RBAC_ADMIN + EDIT)
 */

import { useState } from 'react';

import { ConfirmModal } from './ConfirmModal';
import { USERNAME_PATTERN, loginFailureText, loginStatusOf } from './employeeLoginView';
import { StatusBadge } from '../StatusBadge';
import {
  issueEmployeeCredential,
  resetEmployeePassword,
  type IssueLoginResult,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import type { Employee, IssuedLogin } from '../../lib/rbacAdmin/types';

export interface EmployeeLoginPanelProps {
  readonly employee: Employee;
  readonly canEdit: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** ให้หน้ารายการโหลดใหม่ เพื่ออ่านสถานะบัญชีที่เซิร์ฟเวอร์ยืนยันแล้ว */
  readonly onEmployeeChanged: () => void;
}

export function EmployeeLoginPanel({
  employee,
  canEdit,
  apiBaseUrl,
  fetchImpl,
  onEmployeeChanged,
}: EmployeeLoginPanelProps) {
  const [username, setUsername] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedLogin | null>(null);
  const [failure, setFailure] = useState<RbacAdminFailure | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const status = loginStatusOf(employee.login_status);
  const currentUsername = employee.username ?? null;
  const hasLogin = currentUsername !== null;
  const usernameValid = USERNAME_PATTERN.test(username);

  const receive = (result: IssueLoginResult) => {
    if (result.status === 'ready') {
      setIssued(result.issued);
      setFailure(null);
      setCopyState('idle');
      setUsername('');
      onEmployeeChanged();
    } else {
      setFailure(result);
    }
  };

  const issue = async () => {
    if (!canEdit || busy || !usernameValid) {
      return;
    }
    setBusy(true);
    receive(await issueEmployeeCredential(employee.employee_id, username, { apiBaseUrl, fetchImpl }));
    setBusy(false);
  };

  const reset = async () => {
    if (!canEdit || busy) {
      return;
    }
    setBusy(true);
    const result = await resetEmployeePassword(employee.employee_id, { apiBaseUrl, fetchImpl });
    setConfirmReset(false);
    receive(result);
    setBusy(false);
  };

  const copy = async () => {
    if (issued === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.temporary_password);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <div className="cap-login-panel" data-testid="employee-login-panel">
      <h3 className="cap-card__title">บัญชีล็อกอิน</h3>
      <p className="cap-notice__text" data-testid="employee-login-status">
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>{' '}
        {hasLogin ? (
          <>
            username <code className="cap-table__code">{currentUsername}</code>
          </>
        ) : (
          'พนักงานคนนี้ยังเข้าสู่ระบบ ERP และแอปที่ล็อกอินผ่าน ERP ไม่ได้'
        )}
      </p>

      {issued === null ? null : (
        <div className="cap-notice" data-testid="employee-login-issued" role="region" aria-label="รหัสผ่านชั่วคราว">
          <StatusBadge tone="danger">แสดงครั้งเดียว</StatusBadge>
          <p className="cap-notice__text">
            {issued.username === null ? 'รีเซ็ตรหัสแล้ว' : `ออกบัญชีแล้ว: ${issued.username}`} — ส่งรหัสชั่วคราวนี้ให้พนักงาน
            <strong>เป็นการส่วนตัว</strong> เขาต้องตั้งรหัสใหม่ตอนล็อกอินครั้งแรก ระบบเก็บไว้แค่ค่าแฮช
            ถ้าปิดไปแล้วต้องรีเซ็ตใหม่
          </p>
          <p>
            <code className="cap-table__code" data-testid="employee-login-temporary-password">
              {issued.temporary_password}
            </code>
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="employee-login-copy"
              onClick={() => {
                void copy();
              }}
            >
              คัดลอก
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="employee-login-dismiss"
              onClick={() => {
                setIssued(null);
                setCopyState('idle');
              }}
            >
              ปิด (รหัสจะไม่แสดงอีก)
            </button>
            {copyState === 'copied' ? (
              <span className="cap-notice__note" data-testid="employee-login-copied">
                คัดลอกแล้ว
              </span>
            ) : null}
            {copyState === 'failed' ? (
              <span className="cap-notice__note" data-testid="employee-login-copy-failed">
                คัดลอกอัตโนมัติไม่ได้ เลือกข้อความแล้วคัดลอกเอง
              </span>
            ) : null}
          </div>
        </div>
      )}

      {failure === null ? null : (
        <p className="cap-notice__text" data-testid="employee-login-failure" role="status">
          <StatusBadge tone="danger">ไม่สำเร็จ</StatusBadge> {loginFailureText(failure)}
        </p>
      )}

      {!canEdit ? null : hasLogin ? (
        <button
          type="button"
          className="cap-button cap-button--ghost"
          data-testid="employee-login-reset"
          disabled={busy}
          onClick={() => {
            setFailure(null);
            setConfirmReset(true);
          }}
        >
          รีเซ็ตรหัสผ่าน
        </button>
      ) : employee.status !== 'ACTIVE' ? (
        <p className="cap-notice__note" data-testid="employee-login-inactive">
          ออกบัญชีได้เมื่อสถานะพนักงานเป็น "ใช้งาน"
        </p>
      ) : (
        <form
          className="cap-login-panel__issue"
          data-testid="employee-login-issue-form"
          aria-label="ออกบัญชีล็อกอินให้พนักงานคนนี้"
          onSubmit={(event) => {
            event.preventDefault();
            void issue();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">username</span>
            <input
              className="cap-input"
              data-testid="employee-login-username"
              value={username}
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              onChange={(event) => {
                setUsername(event.target.value.trim().toLowerCase());
              }}
            />
          </label>
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="employee-login-issue"
            disabled={busy || !usernameValid}
          >
            {busy ? 'กำลังออกบัญชี…' : 'ออกบัญชีล็อกอิน'}
          </button>
          <span className="cap-notice__note cap-login-panel__hint">
            a-z ตัวเล็ก ตัวเลข และ . _ - ยาว 3–64 ตัว เช่น somchai.k
          </span>
        </form>
      )}

      <ConfirmModal
        open={confirmReset}
        title="รีเซ็ตรหัสผ่าน"
        confirmLabel="รีเซ็ตรหัส"
        busy={busy}
        onConfirm={() => {
          void reset();
        }}
        onCancel={() => {
          setConfirmReset(false);
        }}
      >
        <p className="cap-notice__text" data-testid="employee-login-reset-warning">
          รีเซ็ตรหัสของ <strong>{employee.display_name}</strong> ({employee.employee_id})? รหัสเดิมใช้ไม่ได้ทันที{' '}
          <strong>ทุก session ถูกตัด</strong> รวมถึงแอปอื่นที่ล็อกอินผ่าน ERP และบัญชีที่ถูกล็อกจะถูกปลดล็อก
        </p>
      </ConfirmModal>
    </div>
  );
}
